import { NOT_APPLICABLE } from 'common/constants/general';
import {
  PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
  SHEET_ALIASES_CONSTANTS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import {
  CreateColumnsParams,
  PerformanceMetricsColumn,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/PerformanceMetrics/types';
import { generateColumnKey, getArrayValue, getNumberValue, getStringValue } from 'utilities';

const { PERFORMANCE_METRICS_SPREADSHEET_COMPANY } = SHEET_ALIASES_CONSTANTS;

const createColumns = (params: CreateColumnsParams) => {
  const { valuationsApproachGpt, financials } = params;

  const transactions = getArrayValue(valuationsApproachGpt?.gpt_transactions);

  // LTM Revenue
  const transactionsLTMRevenue = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ltm_revenue),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ltm_revenue),
      },
    }
  ) as PerformanceMetricsColumn;

  // LTM Revenue Growth
  const transactionsLTMRevenueGrowth = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ltm_revenue_growth),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value:
          financials?.ltm_revenue_growth_rate === NOT_APPLICABLE
            ? financials?.ltm_revenue_growth_rate
            : getNumberValue(financials?.ltm_revenue_growth_rate),
      },
    }
  ) as PerformanceMetricsColumn;

  // NTM Revenue
  const transactionsNTMRevenue = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: Number(current?.ntm_revenue ?? 0) !== 0 ? getNumberValue(current?.ntm_revenue) : NOT_APPLICABLE,
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ntm_revenue),
      },
    }
  ) as PerformanceMetricsColumn;

  // NTM Revenue Growth
  const transactionsNTMRevenueGrowth = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: NOT_APPLICABLE,
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value:
          financials?.ntm_revenue_growth_rate === NOT_APPLICABLE
            ? financials?.ntm_revenue_growth_rate
            : getNumberValue(financials?.ntm_revenue_growth_rate),
      },
    }
  ) as PerformanceMetricsColumn;

  // LTM EBITDA
  const transactionsLTMEBITDA = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ltm_ebitda),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ltm_ebitda),
      },
    }
  ) as PerformanceMetricsColumn;

  // NTM EBITDA
  const transactionsNTMEBITDA = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ntm_ebitda),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ntm_ebitda),
      },
    }
  ) as PerformanceMetricsColumn;

  // LTM Gross Margin
  const transactionsLTMGrossMargin = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ltm_gross_margin),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ltm_gross_margin),
      },
    }
  ) as PerformanceMetricsColumn;

  // LTM EBITDA Margin
  const transactionsLTMEBITDAMargin = transactions?.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        name: getStringValue(current?.target_name),
        prefix: PERFORMANCE_METRICS_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getNumberValue(current?.ltm_ebitda_margin),
      },
    }),
    // Set Company value
    {
      [PERFORMANCE_METRICS_SPREADSHEET_COMPANY]: {
        value: getNumberValue(financials?.ltm_ebitda_margin),
      },
    }
  ) as PerformanceMetricsColumn;

  return [
    transactionsLTMRevenue,
    transactionsLTMRevenueGrowth,
    transactionsNTMRevenue,
    transactionsNTMRevenueGrowth,
    transactionsLTMEBITDA,
    transactionsNTMEBITDA,
    transactionsLTMGrossMargin,
    transactionsLTMEBITDAMargin,
  ];
};

export default createColumns;
